import { ApiError } from "../api";

export interface ConfirmField {
  name: string;
  label: string;
  type: "select" | "text" | "textarea" | "password";
  options?: { value: string; label: string }[];
  value?: string;
  placeholder?: string;
  maxLength?: number;
  required?: boolean;
}

export interface ConfirmOptions {
  title: string;
  /** Texto simples (nunca HTML): pode carregar nomes digitados por jogadores. */
  message: string;
  confirmLabel: string;
  /** Acao destrutiva: botao vermelho. */
  danger?: boolean;
  fields?: ConfirmField[];
  /** Confirmacao reforcada: o botao so libera quando o campo bate com o valor. */
  mustMatch?: { field: string; value: string };
  /**
   * Executa a acao com os valores do formulario. Erros da API aparecem dentro
   * do modal (por campo, quando o servidor diz qual), sem fecha-lo.
   */
  run: (values: Record<string, string>) => Promise<string | void>;
}

/**
 * Modal de confirmacao que roda a acao. Resolve com a mensagem de sucesso, ou
 * null se a pessoa desistiu. Abre por cima de qualquer outro overlay.
 */
export function confirmAction(options: ConfirmOptions): Promise<string | null> {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "overlay confirm-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");

    const panel = document.createElement("form");
    panel.className = "panel confirm-panel";
    panel.noValidate = true;

    const title = document.createElement("h1");
    title.textContent = options.title;
    const message = document.createElement("p");
    message.className = "confirm-message";
    message.textContent = options.message;
    const notice = document.createElement("p");
    notice.className = "notice error hidden";
    panel.append(title, message, notice);

    const inputs = new Map<string, HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>();
    for (const field of options.fields ?? []) {
      const wrap = document.createElement("div");
      wrap.className = "field";
      const id = `confirm-${field.name}`;
      const label = document.createElement("label");
      label.htmlFor = id;
      label.textContent = field.label;

      let input: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (field.type === "select") {
        const select = document.createElement("select");
        for (const opt of field.options ?? []) select.add(new Option(opt.label, opt.value));
        input = select;
      } else if (field.type === "textarea") {
        input = document.createElement("textarea");
        input.rows = 3;
      } else {
        input = document.createElement("input");
        input.type = field.type;
        input.autocomplete = field.type === "password" ? "new-password" : "off";
      }
      input.id = id;
      input.name = field.name;
      if (field.value !== undefined) input.value = field.value;
      if (field.maxLength && !(input instanceof HTMLSelectElement)) input.maxLength = field.maxLength;
      if (field.placeholder && !(input instanceof HTMLSelectElement)) input.placeholder = field.placeholder;
      inputs.set(field.name, input);
      wrap.append(label, input);
      panel.append(wrap);
    }

    const confirmBtn = document.createElement("button");
    confirmBtn.type = "submit";
    confirmBtn.className = options.danger ? "primary danger" : "primary";
    confirmBtn.textContent = options.confirmLabel;
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "secondary";
    cancelBtn.textContent = "Cancelar";
    panel.append(confirmBtn, cancelBtn);

    overlay.append(panel);
    document.body.append(overlay);

    const values = () => Object.fromEntries([...inputs].map(([name, el]) => [name, el.value.trim()]));
    const missingRequired = () =>
      (options.fields ?? []).some((f) => f.required && !inputs.get(f.name)?.value.trim());
    const matches = () => {
      if (!options.mustMatch) return true;
      return inputs.get(options.mustMatch.field)?.value.trim().toLowerCase() === options.mustMatch.value.toLowerCase();
    };
    const refresh = () => {
      confirmBtn.disabled = busy || missingRequired() || !matches();
    };

    let busy = false;
    const close = (result: string | null) => {
      window.removeEventListener("keydown", onKey, true);
      overlay.remove();
      resolve(result);
    };
    // Em captura: o Esc fecha so este modal, nao o overlay de baixo.
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!busy) close(null);
    };
    window.addEventListener("keydown", onKey, true);
    // Teclas digitadas aqui nao chegam ao jogo (o Phaser escuta na window).
    panel.addEventListener("keydown", (event) => event.stopPropagation());

    panel.addEventListener("input", () => {
      clearErrors();
      refresh();
    });
    cancelBtn.addEventListener("click", () => {
      if (!busy) close(null);
    });
    panel.addEventListener("submit", async (event) => {
      event.preventDefault();
      if (confirmBtn.disabled) return;
      busy = true;
      refresh();
      clearErrors();
      try {
        const result = await options.run(values());
        close(result ?? "");
      } catch (error) {
        busy = false;
        refresh();
        const fields = error instanceof ApiError ? error.fields : {};
        let shown = false;
        for (const [name, msg] of Object.entries(fields)) {
          const input = inputs.get(name);
          if (!input) continue;
          const span = document.createElement("span");
          span.className = "error";
          span.textContent = msg;
          input.after(span);
          shown = true;
        }
        if (!shown) {
          notice.textContent = error instanceof Error ? error.message : "Não foi possível concluir a ação.";
          notice.classList.remove("hidden");
        }
      }
    });

    function clearErrors(): void {
      notice.classList.add("hidden");
      panel.querySelectorAll(".field .error").forEach((el) => el.remove());
    }

    refresh();
    (inputs.values().next().value ?? confirmBtn).focus();
  });
}
